import React from 'react'
import ReactDOM from 'react-dom/client'
import { Provider } from 'react-redux'
import { HashRouter } from 'react-router-dom'

import { registerMfaErrorResponseHandler } from 'api/auth/mfa'
import {
    registerUnauthorizedInterceptors,
    injectDependencies,
} from 'api/auth/unauthorizedInterceptor'
import {
    setBrandIdentifierInHeaders,
    setCsrfCookieInHeaders,
} from 'api/requestHeaders'
import { registerSandboxVariantRoutingInterceptor } from 'api/sandboxVariantRouting'
import App from 'App'
import { BrandConfigContextProvider } from 'contexts/BrandConfigContextProvider'
import FeatureFlagContextProvider from 'contexts/FeatureFlagContextProvider'
import LenderContextProvider from 'contexts/LenderContextProvider'
import { createSessionCacheBoundary } from 'contexts/sessionCacheBoundary'
import SessionCacheProvider from 'contexts/SessionCacheProvider'
import store from 'state/store'
import logoutUser from 'state/user/actions/logoutUser'

injectDependencies(store, logoutUser)

const sessionCacheBoundary = createSessionCacheBoundary(() => {
    const { isLoggedIn, data } = store.getState().user

    return {
        isLoggedIn: isLoggedIn === true,
        userId: data?.id,
        dealershipId: data?.dealership?.id,
    }
}, store.subscribe)

if (import.meta.hot) {
    import.meta.hot.dispose(() => sessionCacheBoundary.dispose())
}

import './index.css'

registerUnauthorizedInterceptors()
registerMfaErrorResponseHandler()
setCsrfCookieInHeaders()
setBrandIdentifierInHeaders()
// Sandbox-only: reroute global-axios absolute-URL calls to the active variant.
// Must be registered AFTER the other interceptors so it runs first (LIFO).
registerSandboxVariantRoutingInterceptor()

function RootComponent(): React.ReactElement {
    return (
        <React.StrictMode>
            <HashRouter>
                <Provider store={store}>
                    <SessionCacheProvider boundary={sessionCacheBoundary}>
                        <FeatureFlagContextProvider>
                            <LenderContextProvider>
                                <BrandConfigContextProvider>
                                    <App />
                                </BrandConfigContextProvider>
                            </LenderContextProvider>
                        </FeatureFlagContextProvider>
                    </SessionCacheProvider>
                </Provider>
            </HashRouter>
        </React.StrictMode>
    )
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)

root.render(<RootComponent />)
